// src/features/deadlines/components/UploadModal.tsx
import React, { useState, useEffect } from 'react';
import { X, AlertCircle, Truck, ShieldAlert, Clock } from 'lucide-react';
import { Customer, CustomerCustomTodo, CustomerCustomAttachment } from '../../../shared/types';
import { EmailSenderIdentity } from '../../../services/email/types';
import { TodoSection } from './TodoSection';
import { AttachmentSection } from './AttachmentSection';
import { SenderSelection } from './SenderSelection';
import { CustomMessageInput } from './CustomMessageInput';
import { useAuth } from '../../auth/hooks/useAuth';
import { useSenderManagement } from '../hooks/useSenderManagement';
import { berlinNowLocalString } from '../../../shared/utils/berlinTime';

interface UploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (
    file: File | null, 
    isTestMode?: boolean, 
    additionalFiles?: Record<string, File>, 
    senderIdentity?: EmailSenderIdentity,
    customMessage?: string,
    confirmedTodos?: string[],
    scheduledAtLocal?: string | null // Stufe 3: terminierter Mandantenmail-Versand (Berliner Wanduhrzeit)
  ) => Promise<void>;
  customer: Customer;
  month: number;
  year: number;
  todos?: CustomerCustomTodo[];
  attachments?: CustomerCustomAttachment[];
}

export const UploadModal: React.FC<UploadModalProps> = ({
  isOpen,
  onClose,
  onUpload,
  customer,
  month,
  year,
  todos,
  attachments
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isTestMode, setIsTestMode] = useState(false);
  const [uploadedAttachments, setUploadedAttachments] = useState<Record<string, File>>({});
  const [customMessage, setCustomMessage] = useState('');
  const [confirmedTodos, setConfirmedTodos] = useState<string[]>([]);
  const [filteredTodos, setFilteredTodos] = useState<CustomerCustomTodo[]>([]);
  // FIX (Doppelversand): Wenn die E-Mail bereits versendet wurde, aber ein nachgelagerter
  // Schritt fehlschlug (MAIL_SENT_STATUS_FAILED), wird der Sende-Pfad gesperrt, damit kein
  // zweiter Versand ausgelöst werden kann.
  const [dispatchLocked, setDispatchLocked] = useState(false);
  // Stufe 3: terminierter Versand der Mandantenmail (Sofort/Geplant + Berliner
  // Wanduhrzeit). Gilt nur für email/duo; im Testmodus immer Sofortversand.
  const [scheduleMode, setScheduleMode] = useState<'now' | 'later'>('now');
  const [scheduledLocal, setScheduledLocal] = useState('');

  const { user } = useAuth();
  
  // SAUBERER HOOK FÜR SENDER-MANAGEMENT
  const {
    senders,
    selectedSender,
    setSelectedSender,
    loading: sendersLoading,
    error: sendersError,
    photoValidationErrors,
    senderIdentityUnconfirmed
  } = useSenderManagement(isOpen, user?.email);

  // Filtere To-Dos nach aktuellem Monat
  useEffect(() => {
    if (todos) {
      const filtered = todos.filter(todo => {
        // Wenn active_months null ist, ist das To-Do immer aktiv
        if (!todo.active_months || todo.active_months.length === 0) return true;
        // Prüfe ob der aktuelle Monat in den aktiven Monaten enthalten ist
        return todo.active_months.includes(month);
      });
      setFilteredTodos(filtered);
    }
  }, [todos, month]);

  if (!isOpen) return null;

  const getMonthName = (monthNumber: number): string => {
    const months = [
      'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
      'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
    ];
    return months[monthNumber - 1] || '';
  };

  // Handler für To-Do Bestätigungen
  const handleTodoConfirmation = (todoId: string, confirmed: boolean) => {
    if (confirmed) {
      setConfirmedTodos(prev => [...prev.filter(id => id !== todoId), todoId]);
    } else {
      setConfirmedTodos(prev => prev.filter(id => id !== todoId));
    }
    setError(null); // Clear any existing errors when user makes changes
  };

  // Handler für Attachments
  const handleAttachmentSelect = (attachmentId: string, file: File) => {
    setUploadedAttachments(prev => ({
      ...prev,
      [attachmentId]: file
    }));
    setError(null);
  };

  const handleAttachmentRemove = (attachmentId: string) => {
    setUploadedAttachments(prev => {
      const newFiles = { ...prev };
      delete newFiles[attachmentId];
      return newFiles;
    });
  };

  // Handler für Sender-Auswahl
  const handleSenderChange = (sender: EmailSenderIdentity) => {
    setSelectedSender(sender);
  };

  // Erzwungener Relogin: entfernt das App-Token und lädt die Seite neu.
  // useAuth rekonstruiert danach keinen User mehr → App.tsx zeigt die
  // LoginPage. Nutzt bewusst den vorhandenen Token-Mechanismus, ohne die
  // Auth-Architektur anzufassen.
  const handleForceRelogin = () => {
    localStorage.removeItem('authToken');
    window.location.reload();
  };

  // Prüfe ob alle erforderlichen Attachments hochgeladen wurden
  const requiredAttachments = (attachments || []).filter(att => att.is_required);
  const allRequiredAttachmentsUploaded = requiredAttachments.length > 0 
    ? requiredAttachments.every(att => uploadedAttachments[att.id])
    : true;

  // Prüfe ob alle bestätigungspflichtigen To-Dos bestätigt wurden
  const todosRequiringConfirmation = filteredTodos.filter(todo => todo.requires_confirmation);
  const allRequiredTodosConfirmed = todosRequiringConfirmation.every(
    todo => confirmedTodos.includes(todo.id)
  );
  const hasUnconfirmedTodos = todosRequiringConfirmation.length > 0 && !allRequiredTodosConfirmed;

  const handleUpload = async () => {
    // Prüfe bestätigungspflichtige To-Dos (außer im Testmodus)
    if (!isTestMode && hasUnconfirmedTodos) {
      const unconfirmedCount = todosRequiringConfirmation.filter(
        todo => !confirmedTodos.includes(todo.id)
      ).length;
      setError(`Bitte bestätigen Sie alle erforderlichen To-Dos (${unconfirmedCount} von ${todosRequiringConfirmation.length} noch offen)`);
      return;
    }

    // Prüfe erforderliche Attachments (außer im Testmodus)
    if (!isTestMode && !allRequiredAttachmentsUploaded) {
      setError(`Bitte laden Sie alle erforderlichen Anhänge hoch (${Object.keys(uploadedAttachments).length}/${requiredAttachments.length})`);
      return;
    }

    // Stufe 3: Zeitpunkt-Pflicht bei "Geplant senden" (nur wenn eine Kundenmail
    // rausgeht; kein_versand und Testmodus sind immer Sofortversand). Die
    // Vergangenheits-Prüfung übernimmt der EmailService.
    const isScheduling = !isTestMode && customer.versandmethode !== 'kein_versand' && scheduleMode === 'later';
    if (isScheduling && !scheduledLocal) {
      setError('Bitte wählen Sie einen Sendezeitpunkt.');
      return;
    }
    const effectiveScheduledAtLocal = isScheduling ? scheduledLocal : null;

    // Bei DUO und "kein Versand" ist keine Hauptdatei erforderlich
    if (customer.versandmethode === 'duo' || customer.versandmethode === 'kein_versand') {
      setIsUploading(true);
      setError(null);

      try {
        await onUpload(null, isTestMode, uploadedAttachments, selectedSender, customMessage, confirmedTodos, effectiveScheduledAtLocal);
      } catch (err) {
        // FIX (Doppelversand): War die E-Mail bereits raus, aber ein Folgeschritt schlug fehl,
        // wird der Sende-Pfad gesperrt und eine eindeutige Meldung angezeigt — kein erneuter Versand.
        if (err instanceof Error && err.message === 'MAIL_SENT_STATUS_FAILED') {
          setDispatchLocked(true);
          setError('E-Mail wurde versendet, aber der Status konnte nicht gesetzt werden. Bitte nicht erneut senden — der Monat muss manuell finalisiert werden.');
          setIsUploading(false);
          return;
        }
        setError(err instanceof Error ? err.message : 'Verarbeitung fehlgeschlagen');
        setIsUploading(false);
      }
      return;
    }

    // Standard-Validierung für E-Mail-Versand (außer im Testmodus)
    if (!file && !isTestMode) {
      setError('Bitte wählen Sie eine Datei aus');
      return;
    }

    if (!customer.mandantennummer && !isTestMode) {
      setError('Kunde hat keine Mandantennummer. Upload nicht möglich.');
      return;
    }

    // Validiere PDF (wenn Datei vorhanden)
    if (file && file.type !== 'application/pdf') {
      setError('Nur PDF-Dateien sind erlaubt.');
      return;
    }

    // Validiere Dateinamen nur bei LuG und wenn nicht im Testmodus
    if (file && !isTestMode && (customer.abrechnungsmethode === 'lug' || !customer.abrechnungsmethode)) {
      const fileNameWithoutExtension = file.name.replace(/\.pdf$/i, '');
      const expectedPrefix = `${customer.mandantennummer}_${String(month).padStart(2, '0')}_${year}`;
      
      if (!fileNameWithoutExtension.startsWith(expectedPrefix)) {
        setError(`Der Dateiname muss mit "${expectedPrefix}" beginnen.`);
        return;
      }
    }

    setIsUploading(true);
    setError(null);

    try {
      await onUpload(file, isTestMode, uploadedAttachments, selectedSender, customMessage, confirmedTodos, effectiveScheduledAtLocal);
    } catch (err) {
      // FIX (Doppelversand): War die E-Mail bereits raus, aber ein Folgeschritt schlug fehl,
      // wird der Sende-Pfad gesperrt und eine eindeutige Meldung angezeigt — kein erneuter Versand.
      if (err instanceof Error && err.message === 'MAIL_SENT_STATUS_FAILED') {
        setDispatchLocked(true);
        setError('E-Mail wurde versendet, aber der Status konnte nicht gesetzt werden. Bitte nicht erneut senden — der Monat muss manuell finalisiert werden.');
        setIsUploading(false);
        return;
      }
      setError(err instanceof Error ? err.message : 'Verarbeitung fehlgeschlagen');
      setIsUploading(false);
    }
  };

  // Bereinigte Button-Text-Logik (ohne Emojis)
  const getButtonText = () => {
    if (isTestMode) {
      return customer.versandmethode === 'kein_versand' 
        ? '[TEST] Als abgeschlossen markieren'
        : '[TEST] E-Mail an test@xtax.de senden';
    }
    
    // Stufe 3: Planen-Variante (Monat wird sofort finalisiert, Mail wartet)
    if (customer.versandmethode !== 'kein_versand' && scheduleMode === 'later') {
      return customer.versandmethode === 'duo'
        ? 'DUO-Benachrichtigung planen und abschließen'
        : 'E-Mail-Versand planen und abschließen';
    }

    return customer.versandmethode === 'kein_versand' 
      ? 'Als abgeschlossen markieren'
      : customer.versandmethode === 'duo' 
        ? 'DUO-Benachrichtigung senden'
        : 'E-Mail senden und abschließen';
  };

  const showSenderSelection = customer.versandmethode !== 'kein_versand';

  // Button-Disabled-Status
  const isButtonDisabled = 
    isUploading ||
    dispatchLocked ||
    (!file && customer.versandmethode === 'email' && !isTestMode) || 
    (!customer.mandantennummer && customer.versandmethode !== 'kein_versand' && !isTestMode) ||
    (!allRequiredAttachmentsUploaded && !isTestMode) ||
    (hasUnconfirmedTodos && !isTestMode) ||
    (!isTestMode && customer.versandmethode !== 'kein_versand' && scheduleMode === 'later' && !scheduledLocal) ||
    // Hard-Block: Solange der Absender nicht bewusst bestätigt ist (Sitzung
    // evtl. abgelaufen, still auf den Primär-Mitarbeiter zurueckgefallen), ist
    // der Versand gesperrt — verhindert unbemerktes Senden im falschen Namen.
    (showSenderSelection && senderIdentityUnconfirmed);

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: '#FFFFFF',
        padding: '24px',
        borderRadius: '12px',
        maxWidth: '600px',
        width: '90%',
        maxHeight: '90vh',
        overflowY: 'auto',
        position: 'relative'
      }}>
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '20px',
          paddingBottom: '16px',
          borderBottom: '1px solid #E5E7EB'
        }}>
          <h2 style={{ 
            fontSize: '20px', 
            fontWeight: 'bold', 
            color: '#111827',
            margin: 0
          }}>
            {customer.company} - {getMonthName(month)} {year}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px'
            }}
            disabled={isUploading}
          >
            <X size={24} color="#6B7280" />
          </button>
        </div>

        {/* Customer Info */}
        <div style={{ 
          marginBottom: '16px', 
          padding: '12px', 
          backgroundColor: '#F9FAFB', 
          borderRadius: '8px',
          border: '1px solid #E5E7EB'
        }}>
          <p style={{ fontSize: '14px', color: '#6B7280', margin: 0 }}>
            {customer.beraternummer ? `Berater: ${customer.beraternummer} | ` : ''}
            {customer.mandantennummer ? `Mandant: ${customer.mandantennummer}` : 'Keine Mandantennummer'}
          </p>
        </div>

        {/* WARNUNG wenn bestätigungspflichtige To-Dos vorhanden */}
        {hasUnconfirmedTodos && !isTestMode && (
          <div style={{ 
            marginBottom: '16px', 
            padding: '16px', 
            backgroundColor: '#FEF3C7', 
            borderRadius: '8px',
            border: '1px solid #F59E0B',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <ShieldAlert size={20} color="#F59E0B" />
            <div>
              <p style={{ 
                fontSize: '14px', 
                color: '#92400E', 
                margin: '0 0 4px 0',
                fontWeight: '600'
              }}>
                Bestätigung erforderlich
              </p>
              <p style={{ 
                fontSize: '13px', 
                color: '#B45309', 
                margin: 0,
                lineHeight: '1.4'
              }}>
                Es gibt {todosRequiringConfirmation.length} To-Do{todosRequiringConfirmation.length > 1 ? 's' : ''}, 
                die vor dem Versand bestätigt werden {todosRequiringConfirmation.length > 1 ? 'müssen' : 'muss'}.
              </p>
            </div>
          </div>
        )}

        {/* Postalischer Versand Hinweis */}
        {customer.postalischer_versand && (
          <div style={{ 
            marginBottom: '16px', 
            padding: '16px', 
            backgroundColor: '#EEF2FF', 
            borderRadius: '8px',
            border: '1px solid #C7D2FE',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <Truck size={20} color="#4338CA" />
            <div>
              <p style={{ 
                fontSize: '14px', 
                color: '#4338CA', 
                margin: '0 0 4px 0',
                fontWeight: '600'
              }}>
                Achtung: <strong>Postalischer Versand</strong>
              </p>
              <p style={{ 
                fontSize: '13px', 
                color: '#6366F1', 
                margin: 0,
                lineHeight: '1.4'
              }}>
                DATEV versendet die Auswertungen zusätzlich per Postbrief an den Kunden.
              </p>
            </div>
          </div>
        )}

        {/* Sender Selection - Nur anzeigen wenn E-Mail verschickt wird */}
        {showSenderSelection && (
          <>
            {/* Hard-Block: Absender konnte nicht ueber die eingeloggte
                User-E-Mail bestätigt werden (z. B. abgelaufene Sitzung) und
                wurde per Fallback vorbelegt. Der Versand ist gesperrt, bis der
                User den Absender bewusst auswählt (hebt die Markierung auf)
                oder sich neu anmeldet. */}
            {senderIdentityUnconfirmed && (
              <div style={{
                marginBottom: '16px',
                padding: '16px',
                backgroundColor: '#FEF3C7',
                borderRadius: '8px',
                border: '1px solid #F59E0B',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px'
              }}>
                <ShieldAlert size={20} color="#F59E0B" style={{ flexShrink: 0, marginTop: '2px' }} />
                <div>
                  <p style={{
                    fontSize: '14px',
                    color: '#92400E',
                    margin: '0 0 4px 0',
                    fontWeight: '600'
                  }}>
                    Absender bitte bestätigen
                  </p>
                  <p style={{
                    fontSize: '13px',
                    color: '#B45309',
                    margin: '0 0 10px 0',
                    lineHeight: '1.4'
                  }}>
                    Der Absender konnte nicht automatisch über Ihre Anmeldung bestätigt werden — möglicherweise ist Ihre Sitzung abgelaufen.
                    Voreingestellt ist <strong>{selectedSender?.fullName || 'der primäre Mitarbeiter'}</strong>.
                    Der Versand ist gesperrt, bis Sie den Absender unten bewusst auswählen oder sich neu anmelden.
                  </p>
                  <button
                    type="button"
                    onClick={handleForceRelogin}
                    disabled={isUploading}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: '#F59E0B',
                      color: '#FFFFFF',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: 600,
                      cursor: isUploading ? 'not-allowed' : 'pointer'
                    }}
                  >
                    Neu anmelden
                  </button>
                </div>
              </div>
            )}
            <SenderSelection 
              senders={senders}
              selectedSender={selectedSender}
              onSenderChange={handleSenderChange}
              disabled={isUploading}
              loading={sendersLoading}
              error={sendersError}
              photoValidationErrors={photoValidationErrors}
            />
          </>
        )}

        {/* To-Do Sektion mit Bestätigungsmöglichkeit */}
        <TodoSection 
          todos={filteredTodos} 
          customerName={customer.company}
          onConfirmationChange={handleTodoConfirmation}
          confirmedTodos={confirmedTodos}
          showConfirmation={true}
        />

        {/* Attachment/Upload Sektion */}
        {(customer.versandmethode === 'email' || (attachments && attachments.filter(att => att.is_required).length > 0)) && (
          <AttachmentSection 
            attachments={attachments || []}
            customerName={customer.company}
            uploadedFiles={uploadedAttachments}
            onFileSelect={handleAttachmentSelect}
            onFileRemove={handleAttachmentRemove}
            mainPdfFile={file}
            onMainPdfSelect={setFile}
            onMainPdfRemove={() => setFile(null)}
            versandmethode={customer.versandmethode || 'email'}
            mandantennummer={customer.mandantennummer}
            month={month}
            year={year}
            abrechnungsmethode={customer.abrechnungsmethode}
          />
        )}

        {/* Custom Message Input */}
        {customer.versandmethode !== 'kein_versand' && (
          <div style={{ marginBottom: '16px' }}>
            <CustomMessageInput
              value={customMessage}
              onChange={setCustomMessage}
              customer={customer}
              month={month}
              year={year}
              senderIdentity={selectedSender}
            />
          </div>
        )}

        {/* Bestätigungstext für DUO */}
        {customer.versandmethode === 'duo' && (
          <div style={{ 
            marginBottom: '16px', 
            padding: '16px', 
            backgroundColor: '#F0F8FF', 
            borderRadius: '8px',
            border: '1px solid #B8E0FF'
          }}>
            <p style={{ fontSize: '14px', color: '#0066CC', margin: 0 }}>
              Die Unterlagen werden in DATEV Unternehmen Online bereitgestellt. Der Kunde erhält eine Benachrichtigung per E-Mail.
            </p>
          </div>
        )}

        {/* Bestätigungstext für "kein Versand" */}
        {customer.versandmethode === 'kein_versand' && (
          <div style={{ 
            marginBottom: '16px', 
            padding: '16px', 
            backgroundColor: '#FFF4E5', 
            borderRadius: '8px',
            border: '1px solid #FFD4A3'
          }}>
            <p style={{ fontSize: '14px', color: '#B45309', margin: 0 }}>
              Für diesen Kunden ist kein Versand eingestellt. Möchten Sie den Monat wirklich als abgeschlossen markieren?
            </p>
          </div>
        )}

        {/* Stufe 3: Versandzeitpunkt (Sofort / Geplant) — nur wenn eine Kundenmail
            rausgeht; im Testmodus ausgeblendet, denn Testversand ist immer sofort */}
        {customer.versandmethode !== 'kein_versand' && !isTestMode && (
          <div style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '6px'
            }}>
              Versandzeitpunkt
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <div style={{
                display: 'inline-flex',
                backgroundColor: '#F3F4F6',
                borderRadius: '6px',
                padding: '3px'
              }}>
                <button
                  type="button"
                  onClick={() => setScheduleMode('now')}
                  disabled={isUploading}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '5px',
                    padding: '5px 10px',
                    backgroundColor: scheduleMode === 'now' ? '#FFFFFF' : 'transparent',
                    color: scheduleMode === 'now' ? '#2563EB' : '#6B7280',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: scheduleMode === 'now' ? 600 : 500,
                    cursor: isUploading ? 'not-allowed' : 'pointer',
                    boxShadow: scheduleMode === 'now' ? '0 1px 2px 0 rgba(0,0,0,0.06)' : 'none'
                  }}
                >
                  Sofort senden
                </button>
                <button
                  type="button"
                  onClick={() => setScheduleMode('later')}
                  disabled={isUploading}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '5px',
                    padding: '5px 10px',
                    backgroundColor: scheduleMode === 'later' ? '#FFFFFF' : 'transparent',
                    color: scheduleMode === 'later' ? '#2563EB' : '#6B7280',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: scheduleMode === 'later' ? 600 : 500,
                    cursor: isUploading ? 'not-allowed' : 'pointer',
                    boxShadow: scheduleMode === 'later' ? '0 1px 2px 0 rgba(0,0,0,0.06)' : 'none'
                  }}
                >
                  <Clock size={13} />
                  Geplant senden
                </button>
              </div>
              {scheduleMode === 'later' && (
                <input
                  type="datetime-local"
                  value={scheduledLocal}
                  min={berlinNowLocalString()}
                  onChange={(e) => setScheduledLocal(e.target.value)}
                  disabled={isUploading}
                  style={{
                    padding: '6px 8px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '6px',
                    fontSize: '13px',
                    color: '#374151',
                    backgroundColor: '#FFFFFF'
                  }}
                />
              )}
            </div>
            {scheduleMode === 'later' && (
              <p style={{ fontSize: '12px', color: '#6B7280', margin: '6px 0 0 0', lineHeight: '1.4' }}>
                Der Monat wird sofort finalisiert (inklusive Rechnungsentwurf und Folgeschritten);
                nur die Mandantenmail wird zum gewählten Zeitpunkt (Berliner Zeit) versendet.
                Bis dahin kann sie im Fristen-Dashboard eingesehen und abgebrochen werden.
              </p>
            )}
          </div>
        )}

        {/* Testmodus Checkbox */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px', 
            fontSize: '14px', 
            color: '#374151',
            cursor: 'pointer'
          }}>
            <input
              type="checkbox"
              checked={isTestMode}
              onChange={(e) => setIsTestMode(e.target.checked)}
              disabled={isUploading}
              style={{ 
                width: '16px', 
                height: '16px', 
                cursor: isUploading ? 'not-allowed' : 'pointer'
              }}
            />
            Testmodus aktivieren
          </label>
        </div>

        {/* Error Display */}
        {error && (
          <div style={{ 
            marginBottom: '16px', 
            padding: '12px', 
            backgroundColor: '#FEF2F2', 
            borderRadius: '8px',
            border: '1px solid #FECACA',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <AlertCircle size={16} color="#DC2626" />
            <span style={{ fontSize: '14px', color: '#DC2626' }}>{error}</span>
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            disabled={isUploading}
            style={{
              padding: '8px 16px',
              backgroundColor: '#F3F4F6',
              color: '#374151',
              border: 'none',
              borderRadius: '6px',
              cursor: isUploading ? 'not-allowed' : 'pointer'
            }}
          >
            Abbrechen
          </button>
          <button
            onClick={handleUpload}
            disabled={isButtonDisabled}
            style={{
              padding: '8px 16px',
              backgroundColor: isButtonDisabled ? '#9CA3AF' : '#3B82F6',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '6px',
              cursor: isButtonDisabled ? 'not-allowed' : 'pointer'
            }}
          >
            {isUploading ? 'Verarbeitung läuft...' : getButtonText()}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UploadModal;
